import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Heart, Loader2, Coffee, Gift, Sparkles, Crown, Target, Pencil, Check, X, Share2, PartyPopper } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const TIER_ICON = { coffee: Coffee, cheer: Gift, fan: Sparkles, super: Crown };
const usd = (n) => `$${Number(n || 0).toFixed(2)}`;
const MEDAL = ["🥇", "🥈", "🥉"];

export const SupportGameCard = ({ gameId, game }) => {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => {
    api.get(`/tips/game/${gameId}`).then((r) => setSummary(r.data)).catch(() => setSummary(null));
  }, [gameId]);

  useEffect(() => {
    load();
    api.get("/tips/tiers").then((r) => setTiers(r.data.tiers)).catch(() => {});
  }, [load]);

  // Refresh the goal bar after returning from Stripe (TipJar handles the success toast).
  useEffect(() => {
    if (!params.get("tip_session")) return;
    const t1 = setTimeout(load, 3500);
    const t2 = setTimeout(load, 6500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [params, load]);

  if (!summary) return null;
  const isOwner = !!(user && game && game.owner_id === user.id);
  const goal = summary.goal;
  const raised = summary.game_raised || 0;
  const pct = summary.progress_pct;
  const top = summary.top_supporters || [];

  const tip = async (tier) => {
    setBusy(tier);
    try {
      const { data } = await api.post("/tips/checkout", {
        game_id: gameId, tier, origin_url: window.location.origin, message: msg,
      });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't start the tip"); setBusy(""); }
  };

  const shareWin = async () => {
    const url = `${window.location.origin}/g/${gameId}`;
    const text = `🎉 ${game?.title || "My game"} just hit its funding goal on GameView Studio — thank you supporters! 💚`;
    try {
      if (navigator.share) { await navigator.share({ title: game?.title, text, url }); return; }
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Win copied — share it anywhere! 🎉");
    } catch { /* user cancelled share */ }
  };

  const reached = goal && (pct || 0) >= 100;

  return (
    <div className="rounded-2xl border border-pink-500/30 bg-gradient-to-br from-pink-500/10 to-transparent p-5" data-testid="support-game-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="font-display font-bold flex items-center gap-2"><Heart className="w-5 h-5 text-pink-500 fill-pink-500" /> Support this game</h3>
        {isOwner && !editing && (
          <Button variant="ghost" size="sm" className="rounded-full text-xs h-7" onClick={() => setEditing(true)} data-testid="tip-goal-edit-btn">
            <Pencil className="w-3.5 h-3.5" /> {goal ? "Edit goal" : "Set a goal"}
          </Button>
        )}
      </div>

      {editing ? (
        <GoalEditor gameId={gameId} goal={goal} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />
      ) : goal ? (
        <div className="mb-4" data-testid="tip-goal">
          <div className="flex items-center gap-1.5 text-sm font-semibold"><Target className="w-4 h-4 text-pink-500" /> {goal.title}</div>
          <div className="mt-2 h-3 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-pink-500 to-rose-400 transition-all duration-700"
              style={{ width: `${Math.max(3, pct || 0)}%` }} data-testid="tip-goal-bar" />
          </div>
          <div className="flex items-center justify-between mt-1.5 text-xs">
            <span className="font-bold text-pink-500 tabular-nums" data-testid="tip-goal-raised">{usd(raised)} <span className="text-[hsl(var(--muted-foreground))] font-normal">of {usd(goal.amount)}</span></span>
            <span className="text-[hsl(var(--muted-foreground))]">{summary.game_supporters} supporter{summary.game_supporters === 1 ? "" : "s"}</span>
          </div>
          {reached && (
            <div className="mt-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 text-center" data-testid="goal-reached">
              <div className="flex items-center justify-center gap-1.5 font-bold text-emerald-400"><PartyPopper className="w-4 h-4" /> Goal reached!</div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">Thanks to everyone who chipped in. 💚</p>
              <Button onClick={shareWin} size="sm" className="rounded-full mt-2 bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="share-win-btn">
                <Share2 className="w-3.5 h-3.5" /> Share the win
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="mb-4 text-sm text-[hsl(var(--muted-foreground))]" data-testid="tip-no-goal">
          {raised > 0
            ? <><b className="text-pink-500">{usd(raised)}</b> raised from {summary.game_supporters} supporter{summary.game_supporters === 1 ? "" : "s"}.</>
            : "Be the first to support this creator! 🎉"}
        </div>
      )}

      {top.length > 0 && (
        <div className="mb-4" data-testid="top-supporters">
          <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-1.5">💚 Top supporters</div>
          <div className="space-y-1">
            {top.slice(0, 5).map((s, i) => (
              <div key={s.tipper_id} className="flex items-center gap-2 text-sm" data-testid={`top-supporter-${i}`}>
                <span className="w-5 text-center">{MEDAL[i] || <span className="text-xs text-[hsl(var(--muted-foreground))]">{i + 1}</span>}</span>
                <span className="flex-1 min-w-0 truncate font-medium">{s.name}</span>
                <span className="font-bold text-pink-500 tabular-nums">{usd(s.total)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Button onClick={() => setOpen(true)} disabled={isOwner} data-testid="support-tip-btn"
        title={isOwner ? "You can't tip your own game" : "Support this creator"}
        className="w-full rounded-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-400 hover:to-rose-400 text-white disabled:opacity-40">
        <Heart className="w-4 h-4 fill-white" /> {isOwner ? "Your game" : "Send a tip"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="support-tip-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Heart className="w-5 h-5 text-pink-500 fill-pink-500" /> Support {summary.creator_name || "this creator"}</DialogTitle>
            <DialogDescription>Send a one-tap tip to fuel more great games.{goal ? ` Helping reach: ${goal.title}.` : ""}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            {tiers.map((t) => {
              const Icon = TIER_ICON[t.id] || Heart;
              return (
                <Button key={t.id} onClick={() => tip(t.id)} disabled={!!busy} variant="outline"
                  className="h-auto py-3 flex-col gap-1 rounded-xl border-pink-500/30 hover:bg-pink-500/10" data-testid={`support-tier-${t.id}`}>
                  {busy === t.id ? <Loader2 className="w-5 h-5 animate-spin text-pink-500" /> : <Icon className="w-5 h-5 text-pink-500" />}
                  <span className="font-extrabold text-base">${t.amount.toFixed(0)}</span>
                  <span className="text-[11px] text-[hsl(var(--muted-foreground))]">{t.label}</span>
                </Button>
              );
            })}
          </div>
          <Input value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={200} placeholder="Add a message (optional)" data-testid="support-message" />
          <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Secure checkout via Stripe — you'll return here right after paying.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function GoalEditor({ gameId, goal, onClose, onSaved }) {
  const [amount, setAmount] = useState(goal?.amount ? String(goal.amount) : "");
  const [title, setTitle] = useState(goal?.title || "");
  const [saving, setSaving] = useState(false);

  const save = async (clear = false) => {
    setSaving(true);
    try {
      await api.patch(`/tips/game/${gameId}/goal`, clear ? { amount: 0 } : { amount: parseFloat(amount) || 0, title });
      toast.success(clear ? "Tip goal removed" : "Tip goal saved! 🎯");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mb-4 space-y-2 rounded-xl border border-[hsl(var(--border))] p-3" data-testid="tip-goal-editor">
      <div>
        <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Goal amount (USD)</label>
        <Input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="50" className="mt-1" data-testid="tip-goal-amount" />
      </div>
      <div>
        <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">What are you raising for?</label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} placeholder="e.g. Fund the sequel" className="mt-1" data-testid="tip-goal-title" />
      </div>
      <div className="flex items-center gap-2 pt-1">
        <Button onClick={() => save(false)} disabled={saving || !(parseFloat(amount) > 0)} size="sm" className="rounded-full" data-testid="tip-goal-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save goal
        </Button>
        {goal && <Button onClick={() => save(true)} disabled={saving} size="sm" variant="ghost" className="rounded-full text-red-500" data-testid="tip-goal-remove">Remove</Button>}
        <Button onClick={onClose} disabled={saving} size="sm" variant="ghost" className="rounded-full"><X className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

export default SupportGameCard;
